import {
  DEBT_STATUSES,
} from "../data/mock/dashboardData";
import { daysLateFromDueDate } from "../utils/calendarDate";

export const CUSTOMERS_FILTER_CONFIG = {
  searchKeys: ["name", "phone", "accountNumber", "id", "caseId", "institution", "debt_customer_slug", "numericId"],
  searchPlaceholder: "Search customer ID, case ID, name, institution…",
  fields: [
    { key: "institution", label: "Institution", type: "multi", optionsFrom: "institution", section: "Institution" },
    { key: "channel", label: "Interaction method", type: "multi", optionsFrom: "channel", section: "Engagement" },
    { key: "caseStatus", label: "Status", type: "multi", options: DEBT_STATUSES, section: "Status" },
    { key: "daysOverdue", label: "Days overdue", type: "range", min: 0, max: 120, section: "Amounts & ageing" },
    { key: "amountOwed", label: "Amount owed (₵)", type: "range", min: 0, max: 50000, section: "Amounts & ageing" },
    { key: "amountPaid", label: "Amount paid (₵)", type: "range", min: 0, max: 50000, section: "Amounts & ageing" },
    { key: "outstandingAmount", label: "Outstanding (₵)", type: "range", min: 0, max: 50000, section: "Amounts & ageing" },
  ],
  metrics: [
    { key: "count", label: "Customers", type: "count" },
    { key: "amountOwed", label: "Total owed", type: "sum", field: "amountOwed", format: "currency" },
    { key: "amountPaid", label: "Total paid", type: "sum", field: "amountPaid", format: "currency" },
    { key: "outstandingAmount", label: "Total outstanding", type: "sum", field: "outstandingAmount", format: "currency" },
    { key: "daysOverdue", label: "Avg days overdue", type: "avg", field: "daysOverdue", format: "number" },
    { key: "share", label: "Share of all customers", type: "percent" },
  ],
};

export const CASES_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "institution", "assignedAgent", "accountNumber", "numericId", "portfolioGroupName", "groupId"],
  searchPlaceholder: "Search case ID, customer, institution, group, agent…",
  fields: [
    { key: "status", label: "Case status", type: "multi", optionsFrom: "status", section: "Status" },
    { key: "customer", label: "Customer", type: "multi", optionsFrom: "customer", section: "Customer" },
    { key: "institution", label: "Institution", type: "multi", optionsFrom: "institution", section: "Institution" },
    {
      key: "portfolioGroupName",
      label: "Portfolio group",
      type: "multi",
      optionsFrom: "portfolioGroupName",
      getValue: (row) => row.portfolioGroupName || row.groupId || "—",
      section: "Portfolio",
    },
    {
      key: "assignedAgent",
      label: "Call center agent",
      type: "multi",
      optionsFrom: "assignedAgent",
      getValue: (row) => row.assignedAgent ?? "Unassigned",
      section: "Assignment",
    },
    {
      key: "mandateStatus",
      label: "Mandate status",
      type: "multi",
      optionsFrom: "mandateStatus",
      getValue: (row) => row.mandateStatus || "None",
      section: "Payments",
    },
    {
      key: "planStatus",
      label: "Payment plan",
      type: "multi",
      optionsFrom: "planStatus",
      getValue: (row) => row.planStatus || "None",
      section: "Payments",
    },
    { key: "outstandingAmount", label: "Outstanding (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "amountOwed", label: "Amount owed (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "amountPaid", label: "Amount paid (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "updatedAt", label: "Last updated", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Cases", type: "count" },
    { key: "amountOwed", label: "Total owed", type: "sum", field: "amountOwed", format: "currency" },
    { key: "amountPaid", label: "Total paid", type: "sum", field: "amountPaid", format: "currency" },
    { key: "outstandingAmount", label: "Total outstanding", type: "sum", field: "outstandingAmount", format: "currency" },
    { key: "share", label: "Share of caseload", type: "percent" },
  ],
};

export const PAYMENTS_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "caseId", "reference", "referenceId", "msisdn", "comment", "numericId"],
  searchPlaceholder: "Search payment ID, customer, case, comment…",
  fields: [
    { key: "status", label: "Payment status", type: "multi", options: ["completed", "pending", "failed", "cancelled"], section: "Status" },
    { key: "method", label: "Payment method", type: "multi", optionsFrom: "method", section: "Channel" },
    { key: "paymentType", label: "Payment type", type: "multi", optionsFrom: "paymentType", section: "Channel" },
    { key: "amount", label: "Amount (₵)", type: "range", min: 0, max: 10000, section: "Amounts" },
    { key: "receivedAt", label: "Received date", type: "dateRange", section: "Dates" },
  ],
  metrics: [],
};

export const BATCHES_FILTER_CONFIG = {
  searchKeys: ["name", "institution", "description", "id", "group_slug", "numericId"],
  searchPlaceholder: "Search group, provider, or ID…",
  fields: [
    { key: "institution", label: "Provider", type: "multi", optionsFrom: "institution", section: "Provider" },
    { key: "name", label: "Group", type: "multi", optionsFrom: "name", section: "Group" },
    { key: "status", label: "Status", type: "multi", options: ["completed", "processing"], section: "Status" },
    { key: "accounts", label: "Accounts", type: "range", min: 0, max: 1000, section: "Volume" },
    { key: "totalOwed", label: "Total owed (₵)", type: "range", min: 0, max: 5000000, section: "Amounts" },
    { key: "outstanding", label: "Outstanding (₵)", type: "range", min: 0, max: 5000000, section: "Amounts" },
    { key: "lastUploadedAt", label: "Last upload", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Groups", type: "count" },
    { key: "accounts", label: "Accounts", type: "sum", field: "accounts", format: "number" },
    { key: "totalOwed", label: "Total owed", type: "sum", field: "totalOwed", format: "currency" },
    { key: "totalPaid", label: "Paid", type: "sum", field: "totalPaid", format: "currency" },
    { key: "outstanding", label: "Outstanding", type: "sum", field: "outstanding", format: "currency" },
  ],
};

export const PORTFOLIO_ROWS_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "first_name", "last_name", "accountNumber", "phone", "numericId"],
  searchPlaceholder: "Search debtor, account, case…",
  fields: [
    { key: "status", label: "Case status", type: "multi", optionsFrom: "status", section: "Status" },
    { key: "customer", label: "Customer", type: "multi", optionsFrom: "customer", section: "Customer" },
    { key: "accountNumber", label: "Account", type: "multi", optionsFrom: "accountNumber", section: "Account" },
    { key: "outstandingAmount", label: "Outstanding (₵)", type: "range", section: "Amounts" },
    { key: "amountOwed", label: "Amount owed (₵)", type: "range", section: "Amounts" },
    { key: "amountPaid", label: "Amount paid (₵)", type: "range", section: "Amounts" },
    { key: "daysOverdue", label: "Days overdue", type: "range", section: "Ageing" },
    { key: "dueDate", label: "Due date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Rows", type: "count" },
    { key: "amountOwed", label: "Total owed", type: "sum", field: "amountOwed", format: "currency" },
    { key: "amountPaid", label: "Total paid", type: "sum", field: "amountPaid", format: "currency" },
    { key: "outstandingAmount", label: "Outstanding", type: "sum", field: "outstandingAmount", format: "currency" },
  ],
  title: "Portfolio filters",
};

export const PORTFOLIO_GROUPS_FILTER_CONFIG = {
  searchKeys: ["name", "description", "id", "group_slug", "numericId"],
  searchPlaceholder: "Search portfolio group or ID…",
  fields: [
    { key: "name", label: "Group", type: "multi", optionsFrom: "name", section: "Group" },
    { key: "accounts", label: "Cases", type: "range", min: 0, max: 1000, section: "Volume" },
    { key: "totalOwed", label: "Total owed (₵)", type: "range", min: 0, max: 5000000, section: "Amounts" },
    { key: "outstanding", label: "Outstanding (₵)", type: "range", min: 0, max: 5000000, section: "Amounts" },
    { key: "lastUploadedAt", label: "Last upload", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Groups", type: "count" },
    { key: "accounts", label: "Cases", type: "sum", field: "accounts", format: "number" },
    { key: "totalOwed", label: "Total owed", type: "sum", field: "totalOwed", format: "currency" },
    { key: "outstanding", label: "Outstanding", type: "sum", field: "outstanding", format: "currency" },
  ],
};

export const OPS_ASSIGNABLE_CASES_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "accountNumber", "assignedAgent", "numericId", "portfolioGroupName", "groupId"],
  searchPlaceholder: "Search case, customer, account, group, agent…",
  fields: [
    { key: "customer", label: "Customer name", type: "multi", optionsFrom: "customer", section: "Customer" },
    {
      key: "portfolioGroupName",
      label: "Portfolio group",
      type: "multi",
      optionsFrom: "portfolioGroupName",
      getValue: (row) => row.portfolioGroupName || row.groupId || "—",
      section: "Portfolio",
    },
    {
      key: "assignedAgent",
      label: "Call center agent",
      type: "multi",
      optionsFrom: "assignedAgent",
      getValue: (row) => row.assignedAgent ?? "Unassigned",
      section: "Assignment",
    },
    { key: "status", label: "Case status", type: "multi", optionsFrom: "status", section: "Status" },
    { key: "amountOwed", label: "Amount owed (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "outstandingAmount", label: "Outstanding (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "dueDate", label: "Due date", type: "dateRange", section: "Dates" },
    { key: "updatedAt", label: "Created / updated", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Cases", type: "count" },
    { key: "amountOwed", label: "Total owed", type: "sum", field: "amountOwed", format: "currency" },
    { key: "amountPaid", label: "Paid", type: "sum", field: "amountPaid", format: "currency" },
    { key: "outstandingAmount", label: "Outstanding", type: "sum", field: "outstandingAmount", format: "currency" },
  ],
};

// export const USSD_FILTER_CONFIG = { ... };

export const COMMUNICATIONS_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "caseId", "template"],
  searchPlaceholder: "Search message ID, customer, template…",
  fields: [
    { key: "channel", label: "Channel", type: "multi", options: ["SMS", "WhatsApp", "Email", "Phone call"], section: "Channel" },
    { key: "status", label: "Delivery status", type: "multi", options: ["delivered", "pending", "failed"], section: "Status" },
    { key: "template", label: "Template", type: "multi", optionsFrom: "template", section: "Content" },
    {
      key: "response",
      label: "Customer response",
      type: "select",
      getValue: (row) => (row.response !== "—" ? "yes" : "no"),
      options: [
        { value: "", label: "Any" },
        { value: "yes", label: "Has response" },
        { value: "no", label: "No response" },
      ],
      section: "Engagement",
    },
    { key: "sentAt", label: "Sent date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Messages", type: "count" },
    { key: "share", label: "Share of sends", type: "percent" },
  ],
};

export const CONTACT_CALLS_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "caseId", "agent", "outcome"],
  searchPlaceholder: "Search log ID, customer, agent…",
  fields: [
    { key: "customer", label: "Customer", type: "multi", optionsFrom: "customer", section: "Customer" },
    { key: "agent", label: "Agent", type: "multi", optionsFrom: "agent", section: "Assignment" },
    { key: "type", label: "Channel", type: "multi", optionsFrom: "type", section: "Session" },
    { key: "outcome", label: "Outcome", type: "multi", optionsFrom: "outcome", section: "Outcome" },
    { key: "loggedAt", label: "Logged date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Call logs", type: "count" },
    { key: "share", label: "Share of month", type: "percent" },
  ],
};

export const PTP_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "caseId", "recordedBy"],
  searchPlaceholder: "Search PTP ID, customer, case…",
  fields: [
    { key: "customer", label: "Customer", type: "multi", optionsFrom: "customer", section: "Customer" },
    { key: "status", label: "Promise status", type: "multi", options: ["pending", "kept", "broken"], section: "Status" },
    { key: "recordedBy", label: "Recorded by", type: "multi", optionsFrom: "recordedBy", section: "Assignment" },
    { key: "amount", label: "Promised amount (₵)", type: "range", min: 0, max: 10000, section: "Amounts" },
    { key: "dueDate", label: "Due date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Promises", type: "count" },
    { key: "amount", label: "Total promised", type: "sum", field: "amount", format: "currency" },
    { key: "share", label: "Share of PTPs", type: "percent" },
  ],
};

export const PLANS_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "caseId", "createdBy", "account_number", "plan_id"],
  searchPlaceholder: "Search plan ID, customer, account, case…",
  fields: [
    { key: "customer", label: "Customer", type: "multi", optionsFrom: "customer", section: "Customer" },
    { key: "status", label: "Plan status", type: "multi", options: ["active", "completed", "broken"], section: "Status" },
    { key: "createdBy", label: "Created by", type: "multi", optionsFrom: "createdBy", section: "Assignment" },
    { key: "totalAmount", label: "Plan total (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "amountOwed", label: "Amount owed (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "amountPaid", label: "Amount paid (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "instalments", label: "Instalments", type: "range", min: 1, max: 12, section: "Schedule" },
    { key: "nextDue", label: "Next due", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Payment plans", type: "count" },
    { key: "totalAmount", label: "Total owed", type: "sum", field: "totalAmount", format: "currency" },
    { key: "amountPaid", label: "Total paid", type: "sum", field: "amountPaid", format: "currency" },
    { key: "share", label: "Share of plans", type: "percent" },
  ],
};

export const ESCALATIONS_FILTER_CONFIG = {
  searchKeys: ["id", "caseId", "customer", "reason", "path"],
  searchPlaceholder: "Search escalation ID, case, customer…",
  fields: [
    { key: "legalStatus", label: "Status", type: "multi", optionsFrom: "legalStatus", section: "Status" },
    { key: "customer", label: "Customer", type: "multi", optionsFrom: "customer", section: "Customer" },
    { key: "reason", label: "Escalation reason", type: "multi", optionsFrom: "reason", section: "Reason" },
    { key: "path", label: "Path", type: "multi", optionsFrom: "path", section: "Route" },
    { key: "amountOwed", label: "Amount owed (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "amountPaid", label: "Amount paid (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "escalatedAt", label: "Escalated date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Escalations", type: "count" },
    { key: "amountOwed", label: "Total owed", type: "sum", field: "amountOwed", format: "currency" },
    { key: "amountPaid", label: "Total paid", type: "sum", field: "amountPaid", format: "currency" },
  ],
};

export const SETTLEMENTS_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "caseId", "institution", "type", "reason"],
  searchPlaceholder: "Search request ID, customer, case, institution…",
  fields: [
    { key: "status", label: "Request status", type: "multi", options: ["pending", "approved", "rejected", "completed"], section: "Status" },
    { key: "institution", label: "Institution", type: "multi", optionsFrom: "institution", section: "Institution" },
    { key: "type", label: "Settlement type", type: "multi", options: ["Partial settlement", "Hardship write-off", "Full settlement discount"], section: "Type" },
    { key: "reason", label: "Reason", type: "multi", optionsFrom: "reason", section: "Type" },
    { key: "outstanding", label: "Outstanding (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "proposedAmount", label: "Proposed (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "discountPct", label: "Discount %", type: "range", min: 0, max: 100, section: "Amounts" },
    { key: "submittedAt", label: "Submitted date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Requests", type: "count" },
    { key: "outstanding", label: "Total outstanding", type: "sum", field: "outstanding", format: "currency" },
    { key: "proposedAmount", label: "Total proposed", type: "sum", field: "proposedAmount", format: "currency" },
    { key: "discount", label: "Total discount", type: "sum", field: "discount", format: "currency" },
  ],
};

export const USERS_FILTER_CONFIG = {
  searchKeys: ["name", "email", "id", "role"],
  searchPlaceholder: "Search name, email, role…",
  fields: [
    { key: "role", label: "Role", type: "multi", optionsFrom: "role", section: "Access" },
    { key: "status", label: "Status", type: "multi", optionsFrom: "status", section: "Access" },
    { key: "twoFactor", label: "Two-factor enabled", type: "boolean", section: "Security" },
    { key: "joinedAt", label: "Joined date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Team members", type: "count" },
    { key: "share", label: "Share of team", type: "percent" },
  ],
};

/** Ops team members table (`/dashboard/team`). */
export const TEAM_MEMBERS_FILTER_CONFIG = {
  title: "Team filters",
  searchKeys: ["name", "email", "phone", "phone_number", "role", "admin_slug", "id", "numericId"],
  searchPlaceholder: "Search name, email, phone, or role…",
  fields: [
    {
      key: "role",
      label: "Role",
      type: "multi",
      optionsFrom: "role",
      getValue: (row) => row.role || row.role_slug || "—",
      section: "Access",
    },
    {
      key: "status",
      label: "Status",
      type: "multi",
      options: ["active", "inactive", "invited", "deleted"],
      getValue: (row) => {
        if (row.deleted_at) return "deleted";
        if (row.status === true || row.status === "active") return "active";
        if (row.status === false || row.status === "inactive") return "inactive";
        return String(row.status || "active").toLowerCase();
      },
      section: "Access",
    },
    {
      key: "created_at",
      label: "Added date",
      type: "dateRange",
      getValue: (row) => row.created_at || row.invitedAt,
      section: "Dates",
    },
  ],
};

export const REPORTS_FILTER_CONFIG = {
  searchKeys: ["id", "name", "type", "period"],
  searchPlaceholder: "Search report ID, name, type…",
  fields: [
    { key: "type", label: "Report type", type: "multi", optionsFrom: "type", section: "Classification" },
    { key: "status", label: "Status", type: "multi", options: ["completed", "processing"], section: "Status" },
    { key: "generatedAt", label: "Generated date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Reports", type: "count" },
    { key: "share", label: "Share of library", type: "percent" },
  ],
};

export const ASSIGNMENT_RULES_FILTER_CONFIG = {
  searchKeys: ["id", "name", "criteria", "assignTo"],
  searchPlaceholder: "Search rule ID, name, criteria…",
  fields: [
    { key: "active", label: "Rule status", type: "boolean", section: "Status" },
    { key: "assignTo", label: "Assign to", type: "multi", optionsFrom: "assignTo", section: "Assignment" },
    { key: "casesMatched", label: "Cases matched", type: "range", min: 0, max: 200, section: "Volume" },
  ],
  metrics: [
    { key: "count", label: "Rules", type: "count" },
    { key: "casesMatched", label: "Cases matched", type: "sum", field: "casesMatched", format: "number" },
    { key: "share", label: "Active share", type: "percent" },
  ],
};

export const ASSIGNMENT_CASES_FILTER_CONFIG = {
  searchKeys: ["id", "customer", "assignedAgent", "institution"],
  searchPlaceholder: "Search case ID, customer, agent, institution…",
  fields: [
    { key: "status", label: "Case status", type: "multi", options: ["open", "pending", "active"], section: "Status" },
    {
      key: "assignedAgent",
      label: "Call center agent",
      type: "multi",
      optionsFrom: "assignedAgent",
      getValue: (row) => row.assignedAgent ?? "Unassigned",
      section: "Assignment",
    },
    { key: "institution", label: "Institution", type: "multi", optionsFrom: "institution", section: "Institution" },
    { key: "channel", label: "Interaction method", type: "multi", optionsFrom: "channel", section: "Engagement" },
    { key: "daysOverdue", label: "Days overdue", type: "range", min: 0, max: 120, section: "Amounts & ageing" },
    { key: "outstandingAmount", label: "Outstanding (₵)", type: "range", min: 0, max: 50000, section: "Amounts & ageing" },
  ],
  metrics: [
    { key: "count", label: "Cases in queue", type: "count" },
    { key: "outstandingAmount", label: "Total outstanding", type: "sum", field: "outstandingAmount", format: "currency" },
    { key: "amountOwed", label: "Total owed", type: "sum", field: "amountOwed", format: "currency" },
    { key: "share", label: "Share of queue", type: "percent" },
  ],
};

export const PORTFOLIO_RECORDS_FILTER_CONFIG = {
  searchKeys: ["customer_number", "customer_name", "account_number", "phone", "case_id"],
  searchPlaceholder: "Search customer no., debtor, account, mobile, case…",
  fields: [
    {
      key: "daysLate",
      label: "Days late",
      type: "range",
      min: 0,
      max: 120,
      getValue: (row) => daysLateFromDueDate(row.due_date),
      section: "Ageing",
    },
    { key: "outstanding", label: "Outstanding (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "amount_owed", label: "Amount owed (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "amount_paid", label: "Amount paid (₵)", type: "range", min: 0, max: 50000, section: "Amounts" },
    { key: "due_date", label: "Due date", type: "dateRange", section: "Dates" },
  ],
  metrics: [
    { key: "count", label: "Accounts", type: "count" },
    { key: "amount_owed", label: "Total owed", type: "sum", field: "amount_owed", format: "currency" },
    { key: "amount_paid", label: "Total paid", type: "sum", field: "amount_paid", format: "currency" },
    {
      key: "daysLate",
      label: "Avg days late",
      type: "avg",
      field: "daysLate",
      format: "number",
      getValue: (row) => daysLateFromDueDate(row.due_date),
    },
  ],
};

export { daysLateFromDueDate };

export const AGENTS_FILTER_CONFIG = {
  searchKeys: ["name", "id", "role", "institution"],
  searchPlaceholder: "Search agent name, role, institution…",
  fields: [
    { key: "role", label: "Role", type: "multi", optionsFrom: "role", section: "Access" },
    { key: "status", label: "Status", type: "multi", options: ["active", "on_leave"], section: "Status" },
    { key: "institution", label: "Institution", type: "multi", optionsFrom: "institution", section: "Assignment" },
    { key: "casesAssigned", label: "Cases assigned", type: "range", min: 0, max: 30, section: "Workload" },
    { key: "contactsToday", label: "Contacts today", type: "range", min: 0, max: 30, section: "Workload" },
  ],
  metrics: [
    { key: "count", label: "Agents", type: "count" },
    { key: "casesAssigned", label: "Total cases", type: "sum", field: "casesAssigned", format: "number" },
    { key: "recoveryToday", label: "Recovery today", type: "sum", field: "recoveryToday", format: "currency" },
    { key: "share", label: "Share of team", type: "percent" },
  ],
};

export const INSTITUTIONS_FILTER_CONFIG = {
  searchKeys: ["name", "regNumber", "type", "region", "id", "provider_slug", "code", "numericId"],
  searchPlaceholder: "Search institution ID, name, code, region…",
  fields: [
    { key: "status", label: "Status", type: "multi", options: ["active", "inactive", "deleted", "onboarding"], section: "Status" },
    { key: "region", label: "Region", type: "multi", optionsFrom: "region", section: "Location" },
    { key: "activeCases", label: "Active cases", type: "range", min: 0, max: 500, section: "Volume" },
    { key: "outstanding", label: "Outstanding (₵)", type: "range", min: 0, max: 2000000, section: "Volume" },
  ],
  metrics: [
    { key: "count", label: "Institutions", type: "count" },
    { key: "activeCases", label: "Total cases", type: "sum", field: "activeCases", format: "number" },
    { key: "outstanding", label: "Total outstanding", type: "sum", field: "outstanding", format: "currency" },
  ],
};
